/**
 * Drizzle schema for the Link v1 Postgres database.
 *
 * The schema is the single source of truth for the API server, background
 * workers, and any maintenance scripts. Anything that touches the database
 * imports types from here.
 *
 * Two boundaries are critical:
 *
 * 1. **E2E vs at-rest separation.** `messages_e2e` (ciphertext only, server
 *    cannot decrypt) and `messages_atrest` (ciphertext under the per-Space
 *    `PSK`, server can decrypt for moderation) are deliberately split so the
 *    code paths cannot be confused. Triggers (task 2.2) gate inserts.
 * 2. **Per-Space at-rest keys.** `space_at_rest_keys` rows MUST NOT exist for
 *    `personal_workspace` Spaces. A trigger (task 2.2) enforces this; the
 *    column shape here is the precondition.
 *
 * Conventions:
 *   - Every public-facing row has a numeric `id` (bigint identity, internal
 *     joins) AND a stable `xid` (string, used in URLs and API responses).
 *   - Timestamps are `timestamptz` and default to `now()` where the row is
 *     created server-side.
 *   - `bytea` columns hold raw binary (public keys, hashes, ciphertext, IVs).
 *   - Soft-delete is the default for content via `deleted_at` plus a
 *     `hard_purge_after` worker target.
 */
import { sql } from "drizzle-orm";
import {
  bigint,
  boolean,
  check,
  customType,
  foreignKey,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  primaryKey,
  text,
  timestamp,
  unique,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Custom column types
// ---------------------------------------------------------------------------

/**
 * `bytea` column carrying raw binary data as `Uint8Array` on the JS side.
 * Used for public keys, hashes, AES-GCM IVs, ciphertext envelopes, etc.
 */
const bytea = customType<{ data: Uint8Array; driverData: Buffer }>({
  dataType() {
    return "bytea";
  },
  toDriver(value: Uint8Array): Buffer {
    return Buffer.from(value);
  },
  fromDriver(value: Buffer): Uint8Array {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  },
});

/**
 * Postgres `inet` column carrying an IPv4 or IPv6 address as a string.
 * Stored on `sessions.ip_address` and nulled out by the IP-retention purge
 * worker once `ip_purge_after` has elapsed (Req 22.3).
 */
const inet = customType<{ data: string }>({
  dataType() {
    return "inet";
  },
});

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const spaceTypeEnum = pgEnum("space_type", [
  "personal_workspace",
  "shared_workspace",
  "community",
]);

export const channelTypeEnum = pgEnum("channel_type", [
  "text",
  "snippets",
  "secrets",
  "files",
]);

export const messageStorageEnum = pgEnum("message_storage", ["e2e", "atrest"]);

export const platformKindEnum = pgEnum("platform_kind", [
  "web",
  "desktop",
  "ios",
  "android",
]);

export const accountStatusEnum = pgEnum("account_status", [
  "active",
  "pending_deletion",
  "banned",
  "tombstoned",
]);

export const friendStatusEnum = pgEnum("friend_status", [
  "pending",
  "accepted",
  "blocked",
]);

export const callStatusEnum = pgEnum("call_status", [
  "ringing",
  "active",
  "ended",
  "cancelled",
]);

export const callParticipantStateEnum = pgEnum("call_participant_state", [
  "calling",
  "ringing",
  "accepted",
  "declined",
  "missed",
  "left",
]);

export const abuseReasonEnum = pgEnum("abuse_reason", [
  "spam",
  "harassment",
  "csam",
  "illegal",
  "other",
]);

export const operatorActionEnum = pgEnum("operator_action", [
  "view_report",
  "view_message",
  "ban",
  "unban",
  "delete_message",
  "delete_space",
  "export_request",
]);

export const staffRoleEnum = pgEnum("staff_role", [
  "root_owner",
  "platform_admin",
  "security_admin",
  "trust_safety_lead",
  "moderator",
  "support",
  "auditor",
]);

export const staffStatusEnum = pgEnum("staff_status", [
  "invited",
  "active",
  "suspended",
  "revoked",
]);

export const staffChallengePurposeEnum = pgEnum("staff_challenge_purpose", [
  "registration",
  "authentication",
]);

// ---------------------------------------------------------------------------
// Accounts and identity
// ---------------------------------------------------------------------------

/**
 * Accounts are keyed by the public half of the user's ECDSA P-256
 * Identity_Key. `account_id_hash` is the SHA-256 of `public_key` and is the
 * canonical lookup column for signature-verifying request paths.
 *
 * Req 1.6, 1.7, 22.1.
 */
export const accounts = pgTable(
  "accounts",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    accountIdHash: bytea("account_id_hash").notNull(),
    publicKey: bytea("public_key").notNull(),
    /** Globally unique, user-chosen handle. Null only for pre-migration accounts. */
    username: text("username"),
    /** The last time the global username changed; display names are unrestricted. */
    usernameChangedAt: timestamp("username_changed_at", { withTimezone: true }),
    displayName: text("display_name"),
    avatarUrl: text("avatar_url"),
    avatarObjectKey: text("avatar_object_key"),
    avatarMimeType: text("avatar_mime_type"),
    bannerUrl: text("banner_url"),
    /** User-selected solid profile banner color, stored as #RRGGBB. */
    bannerColor: text("banner_color").notNull().default("#ee7f9f"),
    /** Whether the profile banner is a single color or a two-color gradient. */
    bannerMode: text("banner_mode").notNull().default("solid"),
    bannerSecondaryColor: text("banner_secondary_color").notNull().default("#9b7bd3"),
    customStatus: text("custom_status"),
    /** Optional profile pronouns shown beside the public handle. */
    pronouns: text("pronouns"),
    /** User-selected presence. `invisible` is exposed to peers as offline. */
    presenceStatus: text("presence_status").notNull().default("online"),
    status: accountStatusEnum("status").notNull().default("active"),
    isOperator: boolean("is_operator").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    /** Coarse-grained presence; rounded to the hour to limit metadata, Req 22.1. */
    lastSeenHour: timestamp("last_seen_hour", { withTimezone: true }),
    /** Set when `status='pending_deletion'`, Req 20.x. */
    deletionScheduledAt: timestamp("deletion_scheduled_at", {
      withTimezone: true,
    }),
  },
  (t) => ({
    xidUnique: uniqueIndex("accounts_xid_key").on(t.xid),
    accountIdHashUnique: uniqueIndex("accounts_account_id_hash_key").on(
      t.accountIdHash,
    ),
    usernameUnique: uniqueIndex("accounts_username_lower_key").on(
      sql`lower(${t.username})`,
    ),
    statusIdx: index("accounts_status_idx").on(t.status),
  }),
);

// ---------------------------------------------------------------------------
// Private staff control plane
// ---------------------------------------------------------------------------

/**
 * Administrative identities are intentionally separate from public Link
 * accounts and live in the private control database. `linked_account_xid` is
 * a private association, never a grant by itself. Linked-account authentication
 * additionally requires a console-enabled grant, active staff, a fresh signed
 * challenge, and an allowed IP. There is no cross-database foreign key.
 */
export const staffAccounts = pgTable(
  "staff_accounts",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    adminName: text("admin_name").notNull(),
    displayName: text("display_name").notNull(),
    linkedAccountXid: text("linked_account_xid").notNull(),
    linkLoginEnabled: boolean("link_login_enabled").notNull().default(false),
    role: staffRoleEnum("role").notNull(),
    status: staffStatusEnum("status").notNull().default("invited"),
    inviteTokenHash: bytea("invite_token_hash"),
    inviteExpiresAt: timestamp("invite_expires_at", { withTimezone: true }),
    createdByStaffId: bigint("created_by_staff_id", { mode: "bigint" }),
    activatedAt: timestamp("activated_at", { withTimezone: true }),
    lastLoginAt: timestamp("last_login_at", { withTimezone: true }),
    suspendedAt: timestamp("suspended_at", { withTimezone: true }),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    xidUnique: uniqueIndex("staff_accounts_xid_key").on(t.xid),
    adminNameUnique: uniqueIndex("staff_accounts_admin_name_lower_key").on(
      sql`lower(${t.adminName})`,
    ),
    statusIdx: index("staff_accounts_status_idx").on(t.status),
    linkedAccountIdx: index("staff_accounts_linked_account_xid_idx").on(
      t.linkedAccountXid,
    ),
  }),
);

/** WebAuthn/FIDO2 credentials registered to a staff identity. */
export const staffCredentials = pgTable(
  "staff_credentials",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    staffId: bigint("staff_id", { mode: "bigint" })
      .notNull()
      .references(() => staffAccounts.id, { onDelete: "cascade" }),
    credentialId: text("credential_id").notNull(),
    publicKey: bytea("public_key").notNull(),
    counter: bigint("counter", { mode: "bigint" }).notNull().default(0n),
    transports: jsonb("transports"),
    deviceType: text("device_type").notNull(),
    backedUp: boolean("backed_up").notNull().default(false),
    label: text("label").notNull().default("Security key"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
  },
  (t) => ({
    credentialUnique: uniqueIndex("staff_credentials_credential_id_key").on(
      t.credentialId,
    ),
    staffIdx: index("staff_credentials_staff_idx").on(t.staffId),
  }),
);

/** Short-lived WebAuthn registration and authentication challenges. */
export const staffAuthChallenges = pgTable(
  "staff_auth_challenges",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    staffId: bigint("staff_id", { mode: "bigint" })
      .notNull()
      .references(() => staffAccounts.id, { onDelete: "cascade" }),
    purpose: staffChallengePurposeEnum("purpose").notNull(),
    challenge: text("challenge").notNull(),
    sourceIp: inet("source_ip").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    consumedAt: timestamp("consumed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    xidUnique: uniqueIndex("staff_auth_challenges_xid_key").on(t.xid),
    expiryIdx: index("staff_auth_challenges_expiry_idx").on(t.expiresAt),
  }),
);

/** Short-lived approvals bound to a hashed HttpOnly browser token. */
export const staffLinkChallenges = pgTable("staff_link_challenges", {
  flowId: text("flow_id").primaryKey(),
  nonce: text("nonce").notNull(),
  browserTokenHash: bytea("browser_token_hash").notNull(),
  sourceIp: inet("source_ip").notNull(),
  userAgent: text("user_agent"),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  consumedAt: timestamp("consumed_at", { withTimezone: true }),
  desktopEnabled: boolean("desktop_enabled").notNull().default(false),
  desktopProof: jsonb("desktop_proof").$type<import("../api/staff-login").StaffLoginProof>(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => ({ expiryIdx: index("staff_link_challenges_expiry_idx").on(t.expiresAt) }));

/** HttpOnly staff sessions. Only the SHA-256 token digest is persisted. */
export const staffSessions = pgTable(
  "staff_sessions",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    staffId: bigint("staff_id", { mode: "bigint" })
      .notNull()
      .references(() => staffAccounts.id, { onDelete: "cascade" }),
    tokenHash: bytea("token_hash").notNull(),
    csrfTokenHash: bytea("csrf_token_hash").notNull(),
    linkIdentityKeyHash: bytea("link_identity_key_hash"),
    sourceIp: inet("source_ip").notNull(),
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    lastActivityAt: timestamp("last_activity_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    endedAt: timestamp("ended_at", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("staff_sessions_xid_key").on(t.xid),
    tokenUnique: uniqueIndex("staff_sessions_token_hash_key").on(t.tokenHash),
    staffActiveIdx: index("staff_sessions_staff_active_idx").on(
      t.staffId,
      t.endedAt,
    ),
  }),
);

/** Per-staff public IP/CIDR allowlist. Revocation is historical. */
export const staffIpAllowlist = pgTable(
  "staff_ip_allowlist",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    staffId: bigint("staff_id", { mode: "bigint" })
      .notNull()
      .references(() => staffAccounts.id, { onDelete: "cascade" }),
    cidr: text("cidr").notNull(),
    label: text("label").notNull(),
    createdByStaffId: bigint("created_by_staff_id", { mode: "bigint" }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("staff_ip_allowlist_xid_key").on(t.xid),
    staffIdx: index("staff_ip_allowlist_staff_idx").on(t.staffId),
  }),
);

/**
 * Tamper-evident staff audit trail. A database trigger supplies the hash
 * chain and the application role cannot UPDATE or DELETE these rows.
 */
export const staffAuditLog = pgTable(
  "staff_audit_log",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    actorStaffId: bigint("actor_staff_id", { mode: "bigint" }).references(
      () => staffAccounts.id,
      { onDelete: "set null" },
    ),
    action: text("action").notNull(),
    targetKind: text("target_kind").notNull(),
    targetId: text("target_id").notNull(),
    sourceIp: inet("source_ip").notNull(),
    userAgent: text("user_agent"),
    sessionXid: text("session_xid"),
    reason: text("reason"),
    outcome: text("outcome").notNull().default("success"),
    details: jsonb("details"),
    previousHash: bytea("previous_hash"),
    entryHash: bytea("entry_hash").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    createdIdx: index("staff_audit_log_created_idx").on(t.createdAt),
    actorIdx: index("staff_audit_log_actor_idx").on(t.actorStaffId),
  }),
);

/** Platform-issued identity badges. These are account-level achievements and
 * are intentionally independent from community roles and community tags. */
export const accountBadges = pgTable(
  "account_badges",
  {
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    badgeId: text("badge_id").notNull(),
    grantedAt: timestamp("granted_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    grantedByAccountId: bigint("granted_by_account_id", { mode: "bigint" })
      .references(() => accounts.id, { onDelete: "set null" }),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.accountId, t.badgeId] }),
    badgeIdx: index("account_badges_badge_idx").on(t.badgeId),
  }),
);

/**
 * Tombstone rows left behind after an account is fully purged. The deletion
 * worker writes exactly these three fields and nothing else (Req 20.6,
 * Property 16).
 */
export const accountTombstones = pgTable("account_tombstones", {
  accountIdHash: bytea("account_id_hash").primaryKey(),
  deletedAt: timestamp("deleted_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
  reasonCode: text("reason_code").notNull(),
});

/**
 * Active and historical sessions. Tokens are stored as SHA-256 hashes; the
 * raw token only lives client-side. `ip_address` is nulled by the
 * IP-retention purge worker (Req 22.3) once `ip_purge_after` has passed.
 */
/** Public-account passkeys, distinct from staff credentials. Only ciphertext
 * is backed up; the authenticator PRF secret never reaches this database. */
export const accountPasskeys = pgTable("account_passkeys", {
  credentialId: text("credential_id").primaryKey(),
  accountId: bigint("account_id", { mode: "bigint" }).notNull().references(() => accounts.id, { onDelete: "cascade" }),
  publicKey: bytea("public_key").notNull(),
  identityPublicKey: bytea("identity_public_key").notNull(),
  counter: bigint("counter", { mode: "number" }).notNull(),
  rpId: text("rp_id").notNull(),
  backup: jsonb("backup").$type<import("../api/passkeys").KeyBackup>().notNull(),
  label: text("label").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
}, table => [index("account_passkeys_account_idx").on(table.accountId)]);

export const sessions = pgTable(
  "sessions",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    platform: platformKindEnum("platform").notNull(),
    tokenHash: bytea("token_hash").notNull(),
    ipAddress: inet("ip_address"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    lastActivityAt: timestamp("last_activity_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    endedAt: timestamp("ended_at", { withTimezone: true }),
    ipPurgeAfter: timestamp("ip_purge_after", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("sessions_xid_key").on(t.xid),
    accountActiveIdx: index("sessions_account_active_idx")
      .on(t.accountId)
      .where(sql`${t.endedAt} IS NULL`),
    ipPurgeIdx: index("sessions_ip_purge_idx")
      .on(t.ipPurgeAfter)
      .where(sql`${t.ipAddress} IS NOT NULL`),
  }),
);

// ---------------------------------------------------------------------------
// Spaces, members, roles, invites
// ---------------------------------------------------------------------------

/**
 * Personal_Workspaces, Shared_Workspaces, and Communities all live in
 * `spaces`. DMs are modeled separately in `dm_conversations` because they
 * have different ownership semantics. Soft delete is the default; the
 * background worker hard-purges after `hard_purge_after`.
 *
 * Req 5.1, 5.6, 5.8, 6.1, 6.8, 7.1, 7.7.
 */
export const spaces = pgTable(
  "spaces",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    spaceType: spaceTypeEnum("space_type").notNull(),
    ownerAccountId: bigint("owner_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    name: text("name").notNull(),
    iconUrl: text("icon_url"),
    /** Private object-store location backing the public, stable icon URL. */
    iconObjectKey: text("icon_object_key"),
    iconMimeType: text("icon_mime_type"),
    publicDescription: text("public_description"),
    discoveryEnabled: boolean("discovery_enabled").notNull().default(false),
    /** Additive @everyone permissions used when the account has no roles. */
    defaultPermissions: bigint("default_permissions", { mode: "bigint" })
      .notNull()
      .default(sql`15799648`),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
    hardPurgeAfter: timestamp("hard_purge_after", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("spaces_xid_key").on(t.xid),
    ownerIdx: index("spaces_owner_idx").on(t.ownerAccountId),
    purgeIdx: index("spaces_purge_idx")
      .on(t.hardPurgeAfter)
      .where(sql`${t.deletedAt} IS NOT NULL`),
  }),
);

/** Public community identity tag displayed next to a member's username. A
 * community owns at most one tag; membership is checked whenever it is
 * equipped or resolved for display. */
export const communityTags = pgTable(
  "community_tags",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    code: text("code").notNull(),
    label: text("label").notNull(),
    iconUrl: text("icon_url"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    xidUnique: uniqueIndex("community_tags_xid_key").on(t.xid),
    spaceUnique: uniqueIndex("community_tags_space_key").on(t.spaceId),
  }),
);

/** A user may feature one community tag globally. The issuing community must
 * still contain the user; API reads suppress stale selections immediately. */
export const accountCommunityTags = pgTable("account_community_tags", {
  accountId: bigint("account_id", { mode: "bigint" })
    .primaryKey()
    .references(() => accounts.id, { onDelete: "cascade" }),
  tagId: bigint("tag_id", { mode: "bigint" })
    .notNull()
    .references(() => communityTags.id, { onDelete: "cascade" }),
  equippedAt: timestamp("equipped_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

/**
 * Per-Space at-rest encryption key (`PSK`). Only `shared_workspace` and
 * `community` Spaces ever have a row here; Personal_Workspaces never do.
 * Enforced by a trigger from task 2.2 — the schema cannot enforce this on
 * its own because the predicate spans tables.
 *
 * Req 6.1, 6.8, 7.1, 7.7, 11.6, Property 12(f).
 */
export const spaceAtRestKeys = pgTable("space_at_rest_keys", {
  spaceId: bigint("space_id", { mode: "bigint" })
    .primaryKey()
    .references(() => spaces.id, { onDelete: "cascade" }),
  keyVersion: integer("key_version").notNull().default(1),
  /** AES-256 raw key, KMS-wrapped in production deployments. */
  keyMaterial: bytea("key_material").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
  /** Previous version, retained until re-encryption of all rows completes. */
  rotatedFrom: bytea("rotated_from"),
});

/**
 * Membership in a Space. Personal_Workspaces only ever have one row in this
 * table (the Owner). Other Space types support an arbitrary member set with
 * an `is_admin` flag, plus role rows in `space_member_roles` for Communities.
 *
 * Req 5.8, 6.1, 6.7, 7.1.
 */
export const spaceMembers = pgTable(
  "space_members",
  {
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    joinedAt: timestamp("joined_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    isAdmin: boolean("is_admin").notNull().default(false),
    /** Per-Space name chosen by the member; it may change without a cooldown. */
    nickname: text("nickname"),
    /** Captured at leave time so prior messages still attribute correctly, Req 6.7. */
    lastKnownDisplayName: text("last_known_display_name"),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.spaceId, t.accountId] }),
    accountIdx: index("space_members_account_idx").on(t.accountId),
  }),
);

/**
 * Roles inside a Community. Permissions are a bitmask covering at minimum
 * `manage_channels`, `manage_roles`, `manage_members`, `kick_members`,
 * `ban_members`, `read_messages`, `send_messages`, `manage_messages`, Req 7.2.
 */
export const spaceRoles = pgTable(
  "space_roles",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    /**
     * URL-safe stable identifier surfaced in API responses and on
     * `PATCH /v1/roles/:xid` / `DELETE /v1/roles/:xid` request paths.
     * Internal joins still use `id`; `xid` is the wire identifier the
     * `roleSummarySchema` exposes.
     */
    xid: text("xid").notNull(),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    color: text("color").notNull(),
    nameStyle: text("name_style").$type<"solid" | "gradient" | "glow">().notNull().default("solid"),
    secondaryColor: text("secondary_color"),
    sortPosition: integer("sort_position").notNull(),
    permissions: bigint("permissions", { mode: "bigint" }).notNull(),
  },
  (t) => ({
    xidUnique: uniqueIndex("space_roles_xid_key").on(t.xid),
    spaceNameUnique: unique("space_roles_space_name_key").on(t.spaceId, t.name),
  }),
);

/**
 * Many-to-many join between members and roles. The composite FK to
 * `space_members(space_id, account_id)` keeps this consistent on member
 * removal.
 */
export const spaceMemberRoles = pgTable(
  "space_member_roles",
  {
    spaceId: bigint("space_id", { mode: "bigint" }).notNull(),
    accountId: bigint("account_id", { mode: "bigint" }).notNull(),
    roleId: bigint("role_id", { mode: "bigint" })
      .notNull()
      .references(() => spaceRoles.id, { onDelete: "cascade" }),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.spaceId, t.accountId, t.roleId] }),
    memberFk: foreignKey({
      name: "space_member_roles_member_fk",
      columns: [t.spaceId, t.accountId],
      foreignColumns: [spaceMembers.spaceId, spaceMembers.accountId],
    }).onDelete("cascade"),
  }),
);

/**
 * Per-User Space sidebar order. Each user owns their own ordering; never
 * exposed to other users (Req 17.4).
 */
export const userSidebarOrder = pgTable(
  "user_sidebar_order",
  {
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    sortPosition: integer("sort_position").notNull(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.accountId, t.spaceId] }),
  }),
);

/**
 * Invite tokens. `xid` is the URL-safe token surfaced to invitees. Optional
 * `max_uses` caps the number of accepts; `revoked_at` short-circuits future
 * accepts. Up-to-7-day expiration is enforced at the route layer (Req 6.2).
 */
export const spaceInvites = pgTable(
  "space_invites",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    createdBy: bigint("created_by", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
    maxUses: integer("max_uses"),
    useCount: integer("use_count").notNull().default(0),
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("space_invites_xid_key").on(t.xid),
  }),
);

/** Active community-level bans. A ban both removes membership and prevents
 * every invite token from re-adding the account until this row is lifted. */
export const spaceBans = pgTable(
  "space_bans",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    bannedByAccountId: bigint("banned_by_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    reason: text("reason"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    liftedAt: timestamp("lifted_at", { withTimezone: true }),
    liftedByAccountId: bigint("lifted_by_account_id", { mode: "bigint" })
      .references(() => accounts.id),
  },
  (t) => ({
    xidUnique: uniqueIndex("space_bans_xid_key").on(t.xid),
    activeAccountUnique: uniqueIndex("space_bans_active_account_key")
      .on(t.spaceId, t.accountId)
      .where(sql`${t.liftedAt} IS NULL`),
    spaceCreatedIdx: index("space_bans_space_created_idx").on(
      t.spaceId,
      t.createdAt,
    ),
  }),
);

/** Append-only, community-visible audit history. The database migration
 * revokes UPDATE/DELETE from the application role. */
export const spaceAuditLog = pgTable(
  "space_audit_log",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    actorAccountId: bigint("actor_account_id", { mode: "bigint" })
      .references(() => accounts.id, { onDelete: "set null" }),
    action: text("action").notNull(),
    targetKind: text("target_kind").notNull(),
    targetXid: text("target_xid"),
    details: jsonb("details"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    xidUnique: uniqueIndex("space_audit_log_xid_key").on(t.xid),
    spaceCreatedIdx: index("space_audit_log_space_created_idx").on(
      t.spaceId,
      t.createdAt,
    ),
    actorIdx: index("space_audit_log_actor_idx").on(t.actorAccountId),
  }),
);

/** Custom image emoji owned by a community. */
export const spaceEmojis = pgTable(
  "space_emojis",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    imageUrl: text("image_url").notNull(),
    objectKey: text("object_key").notNull(),
    mimeType: text("mime_type").notNull(),
    createdByAccountId: bigint("created_by_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    xidUnique: uniqueIndex("space_emojis_xid_key").on(t.xid),
    spaceNameUnique: uniqueIndex("space_emojis_space_name_key").on(
      t.spaceId,
      t.name,
    ),
  }),
);

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

/**
 * A Channel belongs to exactly one `space`. The trigger from task 2.2
 * rejects creation of a `secrets` channel inside a `shared_workspace` or
 * `community` Space (Req 10.2).
 */
export const channels = pgTable(
  "channels",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    channelType: channelTypeEnum("channel_type").notNull(),
    name: text("name").notNull(),
    topic: text("topic"),
    sortPosition: integer("sort_position").notNull().default(0),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    xidUnique: uniqueIndex("channels_xid_key").on(t.xid),
    spaceIdx: index("channels_space_idx").on(t.spaceId, t.sortPosition),
  }),
);

// ---------------------------------------------------------------------------
// Messages: split tables for E2E vs at-rest
// ---------------------------------------------------------------------------

/**
 * The metadata row for every message in the system, regardless of storage
 * kind. Plaintext is never stored here — actual content lives in either
 * `messages_e2e` or `messages_atrest`, gated by `storage_kind`. The split
 * exists so the API server has no code path that can accidentally read E2E
 * plaintext.
 *
 * A message lives in EXACTLY ONE of two contexts: a Space-owned `channel`
 * (most messages) or a `dm_channel` (DM messages). The two FK columns are
 * mutually exclusive — exactly one is non-null per row, enforced by the
 * `messages_channel_xor_dm_check` CHECK constraint. This keeps DM messages
 * on the same `messages` + `messages_e2e` storage path the design calls
 * for (Req 15.3, 15.4) without polluting the Space-channel FK.
 *
 * Soft delete via `deleted_at`; the expirations worker hard-purges the
 * matching content row when `now() >= hard_purge_after`. `expires_at` is
 * meaningful only for messages in Secrets_Channels (Req 10.6, 10.7).
 *
 * Req 8.1, 8.6, 8.7, 8.8, 9.6, 10.6, 12.1, 12.4, 15.3, 15.4, 22.5.
 */
export const messages = pgTable(
  "messages",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    /**
     * FK into `channels` for Space-owned messages. Null for DM messages
     * (which carry `dm_channel_id` instead). The CHECK constraint
     * enforces the XOR at the storage layer.
     */
    channelId: bigint("channel_id", { mode: "bigint" }).references(
      () => channels.id,
      { onDelete: "cascade" },
    ),
    /**
     * FK into `dm_channels` for DM messages. Null for Space-owned
     * messages. (Req 15.3, 15.4)
     */
    dmChannelId: bigint("dm_channel_id", { mode: "bigint" }).references(
      () => dmChannels.id,
      { onDelete: "cascade" },
    ),
    /** Nullable when the author has been hard-deleted, Req 6.7 / 20.x. */
    authorAccountId: bigint("author_account_id", { mode: "bigint" }).references(
      () => accounts.id,
    ),
    storageKind: messageStorageEnum("storage_kind").notNull(),
    /** Client AAD id for E2E payloads; null for at-rest messages. */
    e2eContextId: text("e2e_context_id"),
    parentMessageId: bigint("parent_message_id", {
      mode: "bigint",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    editedAt: timestamp("edited_at", { withTimezone: true }),
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
    hardPurgeAfter: timestamp("hard_purge_after", { withTimezone: true }),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("messages_xid_key").on(t.xid),
    parentFk: foreignKey({
      name: "messages_parent_fk",
      columns: [t.parentMessageId],
      foreignColumns: [t.id],
    }).onDelete("set null"),
    channelCreatedIdx: index("messages_channel_created_idx").on(
      t.channelId,
      t.createdAt.desc(),
    ),
    dmChannelCreatedIdx: index("messages_dm_channel_created_idx").on(
      t.dmChannelId,
      t.createdAt.desc(),
    ),
    authorIdx: index("messages_author_idx").on(t.authorAccountId),
    parentIdx: index("messages_parent_idx").on(t.parentMessageId),
    purgeIdx: index("messages_purge_idx")
      .on(t.hardPurgeAfter)
      .where(sql`${t.deletedAt} IS NOT NULL`),
    expiresIdx: index("messages_expires_idx")
      .on(t.expiresAt)
      .where(sql`${t.expiresAt} IS NOT NULL`),
    /**
     * Exactly one of `channel_id` and `dm_channel_id` is non-null per
     * row. Storage-layer enforcement of the
     * Space-message-vs-DM-message split (Req 15.3, 15.4).
     */
    channelXorDmCheck: check(
      "messages_channel_xor_dm_check",
      sql`(${t.channelId} IS NOT NULL)::int + (${t.dmChannelId} IS NOT NULL)::int = 1`,
    ),
  }),
);

/**
 * E2E ciphertext for messages whose channel resolves to a
 * Personal_Workspace, a DM channel, or a Secrets_Channel. The server CANNOT
 * decrypt this content — moderation and search must skip these rows. The
 * trigger from task 2.2 enforces the channel-context predicate on insert.
 *
 * `envelope_kind = 'secrets'` indicates that `envelope` is a SecretsEnvelope
 * wrapping an inner E2eEnvelope (Req 10.3).
 *
 * Req 8.2, 11.5, 15.3, 15.4, 22.5, 22.8.
 */
export const messagesE2e = pgTable(
  "messages_e2e",
  {
    messageId: bigint("message_id", { mode: "bigint" })
      .primaryKey()
      .references(() => messages.id, { onDelete: "cascade" }),
    /** Serialized E2eEnvelope (or SecretsEnvelope wrapping one). */
    envelope: bytea("envelope").notNull(),
    envelopeKind: text("envelope_kind").notNull(),
  },
  (t) => ({
    envelopeKindCheck: check(
      "messages_e2e_envelope_kind_check",
      sql`${t.envelopeKind} IN ('e2e', 'secrets')`,
    ),
  }),
);

/**
 * At-rest ciphertext for messages whose channel resolves to a
 * Shared_Workspace or Community and whose channel type is not `secrets`.
 * Encrypted with AES-256-GCM under the per-Space `PSK` (`PSK` lives in
 * `space_at_rest_keys`). Plaintext is never stored — only ciphertext.
 *
 * `language_hint` lets snippet channels record an override at post time
 * (Req 9.3) without needing to decrypt.
 *
 * Req 8.3, 11.6.
 */
export const messagesAtrest = pgTable("messages_atrest", {
  messageId: bigint("message_id", { mode: "bigint" })
    .primaryKey()
    .references(() => messages.id, { onDelete: "cascade" }),
  spaceKeyVersion: integer("space_key_version").notNull(),
  iv: bytea("iv").notNull(),
  ciphertext: bytea("ciphertext").notNull(),
  languageHint: text("language_hint"),
});

// ---------------------------------------------------------------------------
// Reactions, mentions, pins
// ---------------------------------------------------------------------------

/**
 * Emoji reactions. The set is constrained to the Unicode Emoji 15.0 standard
 * at the route layer (Req 12.2); this table only enforces uniqueness per
 * (message, account, emoji).
 */
export const reactions = pgTable(
  "reactions",
  {
    messageId: bigint("message_id", { mode: "bigint" })
      .notNull()
      .references(() => messages.id, { onDelete: "cascade" }),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    emoji: text("emoji").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.messageId, t.accountId, t.emoji] }),
  }),
);

/**
 * Mentions: stored only for at-rest content where the server can resolve
 * `@account` from the plaintext. For E2E content the client emits a separate
 * `mention.received` realtime event so the server never sees plaintext.
 * Req 12.3.
 */
export const mentions = pgTable(
  "mentions",
  {
    messageId: bigint("message_id", { mode: "bigint" })
      .notNull()
      .references(() => messages.id, { onDelete: "cascade" }),
    mentionedAccountId: bigint("mentioned_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.messageId, t.mentionedAccountId] }),
  }),
);

/**
 * Pinned messages, surfaced as the per-Channel Notes view (Req 14.x).
 * Secrets-channel pins render as `(secret)` placeholders client-side;
 * see Property 9.
 */
export const pins = pgTable(
  "pins",
  {
    channelId: bigint("channel_id", { mode: "bigint" })
      .notNull()
      .references(() => channels.id, { onDelete: "cascade" }),
    messageId: bigint("message_id", { mode: "bigint" })
      .notNull()
      .references(() => messages.id, { onDelete: "cascade" }),
    pinnedBy: bigint("pinned_by", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    pinnedAt: timestamp("pinned_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.channelId, t.messageId] }),
  }),
);

// ---------------------------------------------------------------------------
// Files / attachments
// ---------------------------------------------------------------------------

/**
 * Attachments. `storage_kind` mirrors the parent channel's storage class.
 * For E2E channels: `e2e_key_envelope` carries the per-recipient wrapping of
 * the per-file key; `iv` and `space_key_version` are null. For at-rest
 * channels: `iv` and `space_key_version` are populated; `e2e_key_envelope`
 * is null. `sha256` is over the plaintext on the E2E path (computed
 * client-side and recorded as opaque metadata) and over the ciphertext
 * recorded by the server on the at-rest path.
 *
 * `byte_size <= 25 MB` is enforced at the route layer, Req 11.4.
 *
 * Req 11.3, 11.5, 11.6, 11.7, 11.8.
 */
export const attachments = pgTable(
  "attachments",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    /** Nullable until the upload is finalized and bound to a message. */
    messageId: bigint("message_id", { mode: "bigint" }).references(
      () => messages.id,
      { onDelete: "cascade" },
    ),
    /** Parent context captured at upload creation so finalize cannot drift. */
    channelId: bigint("channel_id", { mode: "bigint" }).references(
      () => channels.id,
      { onDelete: "cascade" },
    ),
    dmChannelId: bigint("dm_channel_id", { mode: "bigint" }).references(
      () => dmChannels.id,
      { onDelete: "cascade" },
    ),
    uploaderAccountId: bigint("uploader_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    storageKind: messageStorageEnum("storage_kind").notNull(),
    filename: text("filename").notNull(),
    /** Drag-drop folder structure preserved per attachment, Req 11.3. */
    folderPath: text("folder_path"),
    mimeType: text("mime_type").notNull(),
    byteSize: bigint("byte_size", { mode: "bigint" }).notNull(),
    sha256: bytea("sha256").notNull(),
    objectKey: text("object_key").notNull(),
    /** Set on at-rest path only. */
    iv: bytea("iv"),
    /** Set on at-rest path only. */
    spaceKeyVersion: integer("space_key_version"),
    /** Set on E2E path only — wraps the per-file key for each recipient. */
    e2eKeyEnvelope: bytea("e2e_key_envelope"),
    /** Client-generated AAD id required to decrypt an E2E file later. */
    e2eContextId: text("e2e_context_id"),
    /** Exact client channel/conversation xid bound into file encryption AAD. */
    encryptionChannelXid: text("encryption_channel_xid").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    finalizedAt: timestamp("finalized_at", { withTimezone: true }),
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("attachments_xid_key").on(t.xid),
    messageIdx: index("attachments_message_idx").on(t.messageId),
    channelIdx: index("attachments_channel_idx").on(t.channelId),
    dmChannelIdx: index("attachments_dm_channel_idx").on(t.dmChannelId),
    contextXorCheck: check(
      "attachments_context_xor_check",
      sql`(${t.channelId} IS NOT NULL)::int + (${t.dmChannelId} IS NOT NULL)::int = 1`,
    ),
  }),
);

/** Per-account notification preferences for a server. */
export const spaceNotificationSettings = pgTable(
  "space_notification_settings",
  {
    spaceId: bigint("space_id", { mode: "bigint" })
      .notNull()
      .references(() => spaces.id, { onDelete: "cascade" }),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    muted: boolean("muted").notNull().default(false),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.spaceId, t.accountId] }),
    accountIdx: index("space_notification_settings_account_idx").on(t.accountId),
  }),
);

/**
 * CSAM hash list checked on at-rest uploads only (Req 11.7, 11.8). E2E
 * uploads are never hashed by the server because the server never sees
 * plaintext. A match returns the generic `policy.upload_rejected` error.
 */
export const csamHashes = pgTable("csam_hashes", {
  sha256: bytea("sha256").primaryKey(),
  source: text("source").notNull(),
  addedAt: timestamp("added_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// ---------------------------------------------------------------------------
// Direct Messages
// ---------------------------------------------------------------------------

/**
 * DMs are modeled as their own family of tables rather than as a Space type.
 * They have no Owner (participants are equals) and are always E2E. Group
 * DMs cap at 10 participants (Req 15.2) — enforced at the route layer.
 *
 * Req 15.1, 15.2, 15.5, 15.6.
 */
export const dmConversations = pgTable(
  "dm_conversations",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    /** false = 1:1; true = group DM up to 10 participants. */
    isGroup: boolean("is_group").notNull(),
    /** Group-chat creator. Null for one-to-one conversations. */
    ownerAccountId: bigint("owner_account_id", { mode: "bigint" }).references(
      () => accounts.id,
      { onDelete: "set null" },
    ),
    /** Optional user-facing group name. */
    name: text("name"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    xidUnique: uniqueIndex("dm_conversations_xid_key").on(t.xid),
  }),
);

/**
 * Per-DM participant rows. `last_read_message_id` is local-canonical: it is
 * stored only on the requesting participant's row and never exposed to other
 * participants (Req 15.6).
 */
export const dmParticipants = pgTable(
  "dm_participants",
  {
    dmId: bigint("dm_id", { mode: "bigint" })
      .notNull()
      .references(() => dmConversations.id, { onDelete: "cascade" }),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    joinedAt: timestamp("joined_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    lastReadMessageId: bigint("last_read_message_id", {
      mode: "bigint",
    }).references(() => messages.id),
    /** Per-account inbox preferences. Never shared with other participants. */
    pinned: boolean("pinned").notNull().default(false),
    muted: boolean("muted").notNull().default(false),
    hiddenAt: timestamp("hidden_at", { withTimezone: true }),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.dmId, t.accountId] }),
    accountIdx: index("dm_participants_account_idx").on(t.accountId),
  }),
);

/**
 * Channels inside a DM. Files_Channels are not allowed in DMs (Req 15.5);
 * the CHECK constraint enforces this at the schema layer.
 */
export const dmChannels = pgTable(
  "dm_channels",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    dmId: bigint("dm_id", { mode: "bigint" })
      .notNull()
      .references(() => dmConversations.id, { onDelete: "cascade" }),
    channelType: channelTypeEnum("channel_type").notNull(),
  },
  (t) => ({
    xidUnique: uniqueIndex("dm_channels_xid_key").on(t.xid),
    typeCheck: check(
      "dm_channels_channel_type_check",
      sql`${t.channelType} IN ('text', 'snippets', 'secrets')`,
    ),
  }),
);

/** Pinned DM messages are separate from community-channel pins so the
 * original community primary key and authorization boundary stay intact. */
export const dmPins = pgTable(
  "dm_pins",
  {
    dmChannelId: bigint("dm_channel_id", { mode: "bigint" })
      .notNull()
      .references(() => dmChannels.id, { onDelete: "cascade" }),
    messageId: bigint("message_id", { mode: "bigint" })
      .notNull()
      .references(() => messages.id, { onDelete: "cascade" }),
    pinnedBy: bigint("pinned_by", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    pinnedAt: timestamp("pinned_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.dmChannelId, t.messageId] }),
  }),
);

// ---------------------------------------------------------------------------
// Voice and video call signaling
// ---------------------------------------------------------------------------

/**
 * Durable call signaling metadata for DM and group-DM calls. Media never
 * passes through these tables; LiveKit carries the encrypted transport after
 * a participant accepts. Keeping ringing state here lets reconnecting clients
 * recover an incoming or missed call without relying on an ephemeral socket.
 */
export const callSessions = pgTable(
  "call_sessions",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    dmId: bigint("dm_id", { mode: "bigint" })
      .notNull()
      .references(() => dmConversations.id, { onDelete: "cascade" }),
    initiatorAccountId: bigint("initiator_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    /**
     * Opaque, client-produced E2E envelope containing the random media key.
     * The API persists and relays these bytes but cannot unwrap them.
     */
    keyEnvelope: bytea("key_envelope").notNull(),
    /** AAD message identifier used by clients when unwrapping keyEnvelope. */
    keyContextId: text("key_context_id").notNull(),
    withVideo: boolean("with_video").notNull().default(false),
    status: callStatusEnum("status").notNull().default("ringing"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    answeredAt: timestamp("answered_at", { withTimezone: true }),
    endedAt: timestamp("ended_at", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("call_sessions_xid_key").on(t.xid),
    openDmUnique: uniqueIndex("call_sessions_open_dm_key")
      .on(t.dmId)
      .where(sql`${t.status} IN ('ringing', 'active')`),
    createdIdx: index("call_sessions_created_idx").on(t.createdAt.desc()),
  }),
);

/** One lifecycle row per invited account, including the caller. */
export const callParticipants = pgTable(
  "call_participants",
  {
    callId: bigint("call_id", { mode: "bigint" })
      .notNull()
      .references(() => callSessions.id, { onDelete: "cascade" }),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    state: callParticipantStateEnum("state").notNull(),
    respondedAt: timestamp("responded_at", { withTimezone: true }),
    joinedAt: timestamp("joined_at", { withTimezone: true }),
    /** Refreshed by connected clients so abandoned calls can be reaped. */
    lastHeartbeatAt: timestamp("last_heartbeat_at", { withTimezone: true }),
    leftAt: timestamp("left_at", { withTimezone: true }),
    notificationReadAt: timestamp("notification_read_at", { withTimezone: true }),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.callId, t.accountId] }),
    accountStateIdx: index("call_participants_account_state_idx").on(
      t.accountId,
      t.state,
    ),
    stateHeartbeatIdx: index("call_participants_state_heartbeat_idx").on(
      t.state,
      t.lastHeartbeatAt,
    ),
  }),
);

// ---------------------------------------------------------------------------
// Friends and blocks
// ---------------------------------------------------------------------------

/**
 * Directed edges expressing the friend-state relationship between two
 * Accounts. A "friend" is a pair of `accepted` rows (a→b and b→a). A "block"
 * is a single `blocked` row from blocker to blocked; blocks are asymmetric
 * (Req 16.6, Property 15).
 */
export const friendEdges = pgTable(
  "friend_edges",
  {
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    otherAccountId: bigint("other_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    status: friendStatusEnum("status").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    pk: primaryKey({ columns: [t.accountId, t.otherAccountId] }),
    noSelfEdge: check(
      "friend_edges_no_self_check",
      sql`${t.accountId} <> ${t.otherAccountId}`,
    ),
    otherIdx: index("friend_edges_other_idx").on(t.otherAccountId, t.status),
  }),
);

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * Per-Account UI settings. Theme is stored as a string (`light` | `dark` |
 * `system`) to keep settings extension cheap; theme/accent enforcement
 * happens client-side, Req 18.x.
 */
export const accountSettings = pgTable("account_settings", {
  accountId: bigint("account_id", { mode: "bigint" })
    .primaryKey()
    .references(() => accounts.id, { onDelete: "cascade" }),
  theme: text("theme").notNull().default("system"),
  accentColor: text("accent_color").notNull().default("indigo"),
  lastSeenRelease: text("last_seen_release"),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

/** Durable account notification inbox. Bodies contain product-safe metadata,
 * never message plaintext or authentication material. */
export const accountNotifications = pgTable(
  "account_notifications",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    accountId: bigint("account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(),
    title: text("title").notNull(),
    body: text("body").notNull(),
    data: jsonb("data"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    readAt: timestamp("read_at", { withTimezone: true }),
  },
  (t) => ({
    xidUnique: uniqueIndex("account_notifications_xid_key").on(t.xid),
    accountCreatedIdx: index("account_notifications_account_created_idx").on(
      t.accountId,
      t.createdAt,
    ),
  }),
);

// ---------------------------------------------------------------------------
// Abuse, bans, audit
// ---------------------------------------------------------------------------

/**
 * Reports filed by users against a Message or an Account. Resolution is
 * recorded inline (`resolved_at`, `resolved_by`); the operator audit log
 * captures the action separately.
 *
 * Req 23.1, 23.2.
 */
export const abuseReports = pgTable(
  "abuse_reports",
  {
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    xid: text("xid").notNull(),
    reporterAccountId: bigint("reporter_account_id", { mode: "bigint" })
      .notNull()
      .references(() => accounts.id),
    reportedMessageId: bigint("reported_message_id", {
      mode: "bigint",
    }).references(() => messages.id),
    reportedAccountId: bigint("reported_account_id", {
      mode: "bigint",
    }).references(() => accounts.id),
    reason: abuseReasonEnum("reason").notNull(),
    note: text("note"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    /** Operator account id; set by `requireOperatorSession` handlers. */
    resolvedBy: bigint("resolved_by", { mode: "bigint" }).references(
      () => accounts.id,
    ),
    /** Opaque staff XID supplied by the isolated control plane. */
    resolvedByStaffXid: text("resolved_by_staff_xid"),
  },
  (t) => ({
    xidUnique: uniqueIndex("abuse_reports_xid_key").on(t.xid),
  }),
);

/**
 * Active and historical bans. A row whose `lifted_at` is null is an active
 * ban. The `bans` table preserves history even after `accounts.status`
 * transitions back to `active`.
 *
 * Req 23.4.
 */
export const bans = pgTable("bans", {
  id: bigint("id", { mode: "bigint" }).primaryKey().generatedAlwaysAsIdentity(),
  accountId: bigint("account_id", { mode: "bigint" })
    .notNull()
    .references(() => accounts.id, { onDelete: "cascade" }),
  reason: text("reason").notNull(),
  createdBy: bigint("created_by", { mode: "bigint" }).references(
    () => accounts.id,
  ),
  /** Opaque staff XID supplied by the isolated control plane. */
  createdByStaffXid: text("created_by_staff_xid"),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
  liftedAt: timestamp("lifted_at", { withTimezone: true }),
  liftedBy: bigint("lifted_by", { mode: "bigint" }).references(
    () => accounts.id,
  ),
  /** Opaque staff XID supplied by the isolated control plane. */
  liftedByStaffXid: text("lifted_by_staff_xid"),
});

/**
 * Append-only audit log for operator actions (Req 23.7). The application
 * role MUST NOT have UPDATE or DELETE on this table — that REVOKE is part
 * of the migrations from task 2.2.
 *
 * `target_id` carries the `xid` of the target row regardless of `target_kind`
 * so admin tooling can render history by entity without joining.
 */
export const operatorAuditLog = pgTable("operator_audit_log", {
  id: bigint("id", { mode: "bigint" }).primaryKey().generatedAlwaysAsIdentity(),
  operatorAccountId: bigint("operator_account_id", { mode: "bigint" })
    .notNull()
    .references(() => accounts.id),
  action: operatorActionEnum("action").notNull(),
  /** 'account' | 'message' | 'space' | 'report'. */
  targetKind: text("target_kind").notNull(),
  targetId: text("target_id").notNull(),
  details: jsonb("details"),
  createdAt: timestamp("created_at", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// ---------------------------------------------------------------------------
// Realtime outbox
// ---------------------------------------------------------------------------

/**
 * The HTTP write path appends to this outbox before publishing to Redis so
 * the WebSocket gateway has a totally-ordered, durable event stream per
 * topic. Resync replays from here for up to 7 days; older rows are GC'd
 * (Req 24.3, 24.4, Property 14).
 *
 * `payload` MUST contain only public-safe data: IDs, timestamps, ciphertext
 * blobs by reference. For E2E content the payload may include the
 * ciphertext envelope so recipients can decrypt locally — never plaintext.
 */
export const realtimeOutbox = pgTable(
  "realtime_outbox",
  {
    /** Monotonic event id; clients track the latest delivered value here. */
    id: bigint("id", { mode: "bigint" })
      .primaryKey()
      .generatedAlwaysAsIdentity(),
    /** e.g. `channel:<xid>`, `account:<xid>`, `dm:<xid>`. */
    topic: text("topic").notNull(),
    /** e.g. `message.created`, `reaction.added`, `friend.request_received`. */
    eventType: text("event_type").notNull(),
    payload: jsonb("payload").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => ({
    topicIdIdx: index("realtime_outbox_topic_id_idx").on(t.topic, t.id),
  }),
);

// ---------------------------------------------------------------------------
// Inferred row types (handy for callers; types follow the table shape).
// ---------------------------------------------------------------------------

export type Account = typeof accounts.$inferSelect;
export type NewAccount = typeof accounts.$inferInsert;
export type AccountTombstone = typeof accountTombstones.$inferSelect;
export type Session = typeof sessions.$inferSelect;
export type Space = typeof spaces.$inferSelect;
export type NewSpace = typeof spaces.$inferInsert;
export type SpaceAtRestKey = typeof spaceAtRestKeys.$inferSelect;
export type SpaceMember = typeof spaceMembers.$inferSelect;
export type SpaceRole = typeof spaceRoles.$inferSelect;
export type SpaceMemberRole = typeof spaceMemberRoles.$inferSelect;
export type SpaceBan = typeof spaceBans.$inferSelect;
export type SpaceAuditEntry = typeof spaceAuditLog.$inferSelect;
export type SpaceEmoji = typeof spaceEmojis.$inferSelect;
export type UserSidebarOrder = typeof userSidebarOrder.$inferSelect;
export type SpaceInvite = typeof spaceInvites.$inferSelect;
export type Channel = typeof channels.$inferSelect;
export type Message = typeof messages.$inferSelect;
export type NewMessage = typeof messages.$inferInsert;
export type MessageE2e = typeof messagesE2e.$inferSelect;
export type MessageAtrest = typeof messagesAtrest.$inferSelect;
export type Reaction = typeof reactions.$inferSelect;
export type Mention = typeof mentions.$inferSelect;
export type Pin = typeof pins.$inferSelect;
export type Attachment = typeof attachments.$inferSelect;
export type CsamHash = typeof csamHashes.$inferSelect;
export type DmConversation = typeof dmConversations.$inferSelect;
export type DmParticipant = typeof dmParticipants.$inferSelect;
export type DmChannel = typeof dmChannels.$inferSelect;
export type DmPin = typeof dmPins.$inferSelect;
export type CallSession = typeof callSessions.$inferSelect;
export type CallParticipant = typeof callParticipants.$inferSelect;
export type FriendEdge = typeof friendEdges.$inferSelect;
export type AccountSettings = typeof accountSettings.$inferSelect;
export type AccountNotification = typeof accountNotifications.$inferSelect;
export type StaffAccount = typeof staffAccounts.$inferSelect;
export type StaffCredential = typeof staffCredentials.$inferSelect;
export type StaffAuthChallenge = typeof staffAuthChallenges.$inferSelect;
export type StaffSession = typeof staffSessions.$inferSelect;
export type StaffIpAllowlistEntry = typeof staffIpAllowlist.$inferSelect;
export type StaffAuditLog = typeof staffAuditLog.$inferSelect;
export type AbuseReport = typeof abuseReports.$inferSelect;
export type Ban = typeof bans.$inferSelect;
export type OperatorAuditLog = typeof operatorAuditLog.$inferSelect;
export type RealtimeOutbox = typeof realtimeOutbox.$inferSelect;
